export const eventFlowStyles = {
  // Root
  root: {
    minHeight: "100vh",
    backgroundColor: "#11193eff",
    color: "#FFFFFF",
    fontFamily: "'Inter', 'Roboto', sans-serif",
    position: "relative",
    overflowX: "hidden",
    paddingTop: { xs: "80px", md: "110px" },
  },

  // Confetti Container
  confettiContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    zIndex: 9999,
    overflow: "hidden",
  },

  // Header - Matching Reference Design
  header: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    width: "100%",
    zIndex: 1100,
    backgroundColor: "#11193eff",
    backdropFilter: "blur(20px)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
    transition: "all 0.3s ease",
    py: 1.5,
  },

  headerScrolled: {
    py: 1,
    boxShadow: "0 4px 30px rgba(232, 67, 147, 0.3)",
    backgroundColor: "#11193eff",
    borderBottom: "1px solid rgba(232, 67, 147, 0.2)",
  },

  headerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
  },

  logo: {
    cursor: "pointer",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },

  menuButton: {
    color: "#FFFFFF",
    backgroundColor: "#11193eff",
    "&:hover": {
      backgroundColor: "rgba(232, 67, 147, 0.2)",
    },
  },

  desktopNav: {
    display: "flex",
    gap: 0.5,
    flex: 1,
    justifyContent: "center",
    flexWrap: "wrap",
    alignItems: "center",
  },

  navButton: {
    color: "#FFFFFF",
    fontWeight: 500,
    fontSize: { md: "0.75rem", lg: "0.875rem" },
    textTransform: "none",
    px: { md: 1.5, lg: 2 },
    py: 1,
    borderRadius: 1,
    transition: "all 0.3s ease",
    minWidth: "auto",
    whiteSpace: "nowrap",
    position: "relative",
    "&:hover": {
      color: "#E84393",
      backgroundColor: "rgba(232, 67, 147, 0.1)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 8,
      left: "50%",
      transform: "translateX(-50%)",
      width: "0%",
      height: "2px",
      background: "linear-gradient(90deg, #E84393 0%, #6C5CE7 100%)",
      transition: "width 0.3s ease",
    },
    "&:hover::after": {
      width: "60%",
    },
  },

  navButtonActive: {
    color: "#E84393",
    fontWeight: 700,
    "&::after": {
      width: "60%",
    },
  },

  headerIconButton: {
    color: "#FFFFFF",
    transition: "all 0.3s ease",
    "&:hover": {
      color: "#E84393",
      backgroundColor: "rgba(232, 67, 147, 0.1)",
    },
  },

  buyTicketButton: {
    background: "linear-gradient(135deg, #E84393 0%, #C2185B 100%)",
    color: "#FFFFFF",
    fontWeight: 700,
    textTransform: "none",
    px: 3,
    py: 1,
    borderRadius: 25,
    fontSize: "0.875rem",
    boxShadow: "0 4px 15px rgba(232, 67, 147, 0.4)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(232, 67, 147, 0.6)",
      background: "linear-gradient(135deg, #C2185B 0%, #E84393 100%)",
    },
  },

  // Back Button
  backButton: {
    color: "#E84393",
    fontWeight: 600,
    textTransform: "none",
    mb: 3,
    "&:hover": {
      backgroundColor: "rgba(232, 67, 147, 0.1)",
    },
  },

  // Hero Split Section - Matching Reference Design
  heroSplitSection: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
    gap: { xs: 4, md: 6 },
    alignItems: "center",
    minHeight: { md: "600px" },
    mb: 8,
    position: "relative",
  },

  heroTextSection: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    pr: { md: 4 },
  },

  heroMusicTag: {
    display: "inline-block",
    width: "fit-content",
    px: 2,
    py: 0.5,
    borderRadius: 20,
    backgroundColor: "rgba(232, 67, 147, 0.15)",
    color: "#E84393",
    fontSize: "0.65rem",
    fontWeight: 700,
    letterSpacing: "1.5px",
    border: "1px solid rgba(232, 67, 147, 0.3)",
  },

  heroLargeTitle: {
    fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3rem", lg: "3.5rem" },
    fontWeight: 900,
    lineHeight: 1.1,
    color: "#FFFFFF",
    textTransform: "uppercase",
  },

  heroOutlineText: {
    WebkitTextStroke: "1.5px #FFFFFF",
    WebkitTextFillColor: "transparent",
    display: "block",
    fontStyle: "italic",
    fontFamily: "serif",
    letterSpacing: "2px",
  },

  heroDescription: {
    fontSize: { xs: "0.875rem", md: "0.95rem" },
    color: "#A0AEC0",
    lineHeight: 1.7,
    maxWidth: "450px",
  },

  heroInfoBoxes: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    my: 1.5,
  },

  heroInfoItem: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },

  heroInfoText: {
    fontSize: "0.85rem",
    color: "#CBD5E0",
    fontWeight: 500,
  },

  heroPurchaseButton: {
    background: "linear-gradient(135deg, #E84393 0%, #C2185B 100%)",
    color: "#FFFFFF",
    fontWeight: 700,
    textTransform: "none",
    px: 3,
    py: 1.2,
    borderRadius: 25,
    fontSize: "0.9rem",
    boxShadow: "0 4px 20px rgba(232, 67, 147, 0.5)",
    transition: "all 0.3s ease",
    width: "fit-content",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 8px 30px rgba(232, 67, 147, 0.7)",
      background: "linear-gradient(135deg, #C2185B 0%, #E84393 100%)",
    },
  },

  heroComingSoonButton: {
    background: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(10px)",
    color: "#FFFFFF",
    fontWeight: 700,
    textTransform: "none",
    px: 4,
    py: 1.2,
    borderRadius: 25,
    fontSize: "0.9rem",
    border: "2px solid rgba(232, 67, 147, 0.5)",
    boxShadow: "0 0 20px rgba(232, 67, 147, 0.2)",
    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    width: "fit-content",
    letterSpacing: "2px",
    "&:hover": {
      transform: "translateY(-5px) scale(1.02)",
      borderColor: "#E84393",
      boxShadow: "0 0 30px rgba(232, 67, 147, 0.6)",
      background: "rgba(232, 67, 147, 0.15)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: -2,
      left: -2,
      right: -2,
      bottom: -2,
      borderRadius: 25,
      background: "linear-gradient(45deg, #E84393, #6C5CE7)",
      zIndex: -1,
      opacity: 0.3,
      transition: "opacity 0.3s ease",
    },
    "&:hover::after": {
      opacity: 0.6,
    },
  },

  heroImageSection: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  heroImageFrame: {
    position: "relative",
    width: "100%",
    maxWidth: "400px",
    aspectRatio: "3/4",
    borderRadius: 4,
    overflow: "hidden",
    border: "3px solid rgba(232, 67, 147, 0.3)",
    boxShadow: "0 20px 60px rgba(232, 67, 147, 0.4)",
    "&::before": {
      content: '""',
      position: "absolute",
      top: -20,
      right: -20,
      width: "150px",
      height: "150px",
      background: "radial-gradient(circle, rgba(232, 67, 147, 0.3) 0%, transparent 70%)",
      pointerEvents: "none",
      zIndex: -1,
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -20,
      left: -20,
      width: "150px",
      height: "150px",
      background: "radial-gradient(circle, rgba(108, 92, 231, 0.3) 0%, transparent 70%)",
      pointerEvents: "none",
      zIndex: -1,
    },
  },

  heroMainImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.5s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },

  // Contact Details in Header
  contactDetails: {
    display: { xs: "none", lg: "flex" },
    alignItems: "center",
    gap: 2,
    backgroundColor: "rgba(232, 67, 147, 0.1)",
    padding: "12px 20px",
    borderRadius: 3,
    border: "1px solid rgba(232, 67, 147, 0.3)",
    backdropFilter: "blur(10px)",
  },

  contactIconBox: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #E84393 0%, #6C5CE7 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    flexShrink: 0,
    boxShadow: "0 4px 15px rgba(232, 67, 147, 0.5)",
  },

  contactInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
  },

  contactText: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: "#FFFFFF",
    letterSpacing: "0.5px",
  },

  contactEmail: {
    fontSize: "0.85rem",
    color: "#A0AEC0",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    transition: "all 0.3s ease",
    "&:hover": {
      color: "#E84393",
    },
  },

  // Hero Banner (kept for compatibility)
  heroBanner: {
    position: "relative",
    width: "100%",
    height: { xs: "400px", sm: "500px", md: "600px" },
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: 6,
  },

  heroImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  heroOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "linear-gradient(135deg, rgba(232, 67, 147, 0.8) 0%, rgba(108, 92, 231, 0.8) 100%)",
  },

  heroContainer: {
    position: "relative",
    zIndex: 2,
    textAlign: "center",
  },

  heroBannerTag: {
    display: "inline-block",
    px: 3,
    py: 0.5,
    borderRadius: 20,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    backdropFilter: "blur(10px)",
    fontSize: "0.875rem",
    fontWeight: 600,
    letterSpacing: "2px",
    mb: 2,
    border: "1px solid rgba(255, 255, 255, 0.3)",
  },

  heroBannerTitle: {
    fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
    fontWeight: 900,
    color: "#FFFFFF",
    mb: 2,
    textShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
  },

  heroBannerSubtitle: {
    fontSize: { xs: "1rem", md: "1.3rem" },
    color: "rgba(255, 255, 255, 0.9)",
    fontWeight: 500,
  },

  // Hero Content
  heroContent: {
    textAlign: "center",
    maxWidth: "900px",
    mx: "auto",
    mb: 8,
  },

  heroTag: {
    display: "inline-block",
    px: 2.5,
    py: 0.5,
    borderRadius: 20,
    backgroundColor: "rgba(232, 67, 147, 0.15)",
    color: "#E84393",
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "2px",
    mb: 2,
    border: "1px solid rgba(232, 67, 147, 0.3)",
  },

  heroTitle: {
    fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem" },
    fontWeight: 900,
    background: "linear-gradient(135deg, #FFFFFF 0%, #E84393 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    mb: 2,
    lineHeight: 1.2,
  },

  heroSubtitle: {
    fontSize: { xs: "1.1rem", md: "1.3rem" },
    color: "#A0AEC0",
    fontWeight: 500,
    mb: 2,
  },

  // Buttons
  primaryButton: {
    background: "linear-gradient(135deg, #E84393 0%, #6C5CE7 100%)",
    color: "#FFFFFF",
    fontWeight: 700,
    textTransform: "none",
    px: 4,
    py: 1.5,
    borderRadius: 2,
    fontSize: "1rem",
    boxShadow: "0 4px 15px rgba(232, 67, 147, 0.4)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(232, 67, 147, 0.6)",
      background: "linear-gradient(135deg, #6C5CE7 0%, #E84393 100%)",
    },
  },

  secondaryButton: {
    borderColor: "#E84393",
    color: "#E84393",
    fontWeight: 700,
    textTransform: "none",
    px: 4,
    py: 1.5,
    borderRadius: 2,
    fontSize: "1rem",
    borderWidth: 2,
    "&:hover": {
      borderWidth: 2,
      backgroundColor: "rgba(232, 67, 147, 0.1)",
      borderColor: "#E84393",
    },
  },

  // Sections
  whyJoinSection: {
    mb: 8,
  },

  sectionTitle: {
    fontSize: { xs: "2rem", md: "2.5rem" },
    fontWeight: 800,
    textAlign: "center",
    mb: 5,
    background: "linear-gradient(135deg, #FFFFFF 0%, #E84393 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },

  featureGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
    gap: 3,
  },

  featureCard: {
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(20px)",
    borderRadius: 3,
    p: 4,
    border: "1px solid rgba(232, 67, 147, 0.2)",
    textAlign: "center",
    transition: "all 0.3s ease",
    "&:hover": {
      border: "1px solid rgba(232, 67, 147, 0.5)",
      transform: "translateY(-5px)",
      boxShadow: "0 10px 30px rgba(232, 67, 147, 0.2)",
    },
  },

  featureTitle: {
    fontSize: "1.3rem",
    fontWeight: 700,
    color: "#E84393",
    mb: 1.5,
  },

  featureDescription: {
    fontSize: "0.95rem",
    color: "#A0AEC0",
    lineHeight: 1.7,
  },

  // Stats Section
  statsSection: {
    background: "linear-gradient(135deg, rgba(232, 67, 147, 0.1) 0%, rgba(108, 92, 231, 0.1) 100%)",
    borderRadius: 3,
    p: { xs: 4, md: 6 },
    mb: 8,
    border: "1px solid rgba(232, 67, 147, 0.2)",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
    gap: 4,
  },

  statItem: {
    textAlign: "center",
  },

  statNumber: {
    fontSize: { xs: "2.5rem", md: "3.5rem" },
    fontWeight: 900,
    background: "linear-gradient(135deg, #E84393 0%, #6C5CE7 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    lineHeight: 1,
    mb: 1,
  },

  statLabel: {
    fontSize: "0.95rem",
    color: "#A0AEC0",
    fontWeight: 600,
  },

  // Info Section
  infoSection: {
    mb: 6,
  },

  infoGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
    gap: 3,
  },

  infoCard: {
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(20px)",
    borderRadius: 3,
    p: 3,
    border: "1px solid rgba(255, 255, 255, 0.1)",
    textAlign: "center",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
      border: "1px solid rgba(232, 67, 147, 0.5)",
    },
  },

  infoCardTitle: {
    fontSize: "0.875rem",
    color: "#A0AEC0",
    mb: 1,
  },

  infoCardValue: {
    fontSize: "1rem",
    fontWeight: 700,
    color: "#FFFFFF",
  },

  // Content Cards
  pageTitle: {
    fontSize: { xs: "2rem", md: "2.5rem" },
    fontWeight: 800,
    mb: 4,
    background: "linear-gradient(135deg, #FFFFFF 0%, #E84393 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },

  contentCard: {
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(20px)",
    borderRadius: 3,
    p: { xs: 3, md: 4 },
    border: "1px solid rgba(255, 255, 255, 0.1)",
    transition: "all 0.3s ease",
    "&:hover": {
      border: "1px solid rgba(232, 67, 147, 0.3)",
    },
  },

  cardTitle: {
    fontSize: { xs: "1.3rem", md: "1.5rem" },
    fontWeight: 700,
    color: "#E84393",
    mb: 2,
  },

  cardText: {
    fontSize: "1rem",
    color: "#A0AEC0",
    lineHeight: 1.8,
    marginBottom: 2,
    justifyContent: "center",
    alignItems: "center",
  },
  cardText1: {
    fontSize: "1rem",
    color: "#A0AEC0",
    lineHeight: 1.8,
    marginBottom: 2,
    justifyContent: "start",
    alignItems: "start",
  },
  cardText2: {
    fontSize: "1rem",
    color: "#A0AEC0",
    lineHeight: 1.8,
    marginBottom: 2,
  },
  // Achievements
  achievementList: {
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    mt: 2,
  },

  achievementItem: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    p: 1.5,
    background: "rgba(0, 184, 148, 0.05)",
    borderRadius: 2,
    border: "1px solid rgba(0, 184, 148, 0.2)",
  },

  checkIcon: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #00B894 0%, #55EFC4 100%)",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "1rem",
    flexShrink: 0,
  },

  achievementText: {
    fontSize: "1rem",
    color: "#CBD5E0",
  },

  // Invitation Card
  invitationCard: {
    background: "linear-gradient(135deg, rgba(232, 67, 147, 0.08) 0%, rgba(108, 92, 231, 0.08) 100%)",
    backdropFilter: "blur(20px)",
    borderRadius: 3,
    p: { xs: 3, md: 5 },
    border: "1px solid rgba(232, 67, 147, 0.3)",
  },

  invitationTitle: {
    fontSize: { xs: "1.5rem", md: "2rem" },
    fontWeight: 700,
    textAlign: "center",
    mb: 3,
    color: "#FFFFFF",
  },

  eventDetailsBox: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
    gap: 3,
    my: 3,
  },

  eventDetail: {
    textAlign: "center",
    p: 3,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 2,
    border: "1px solid rgba(255, 255, 255, 0.1)",
  },

  eventDetailLabel: {
    fontSize: "0.875rem",
    color: "#A0AEC0",
    my: 1,
  },

  eventDetailValue: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#FFFFFF",
  },

  categoryChip: {
    bgcolor: "rgba(232, 67, 147, 0.15)",
    color: "#E84393",
    fontWeight: 600,
    borderRadius: 2,
    border: "1px solid rgba(232, 67, 147, 0.3)",
  },

  signatureText: {
    color: "#CBD5E0",
    fontSize: "0.95rem",
    lineHeight: 1.8,
  },

  // Registration
  registrationGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
    gap: 3,
    mt: 3,
  },

  dateBox: {
    textAlign: "center",
    p: 3,
    background: "rgba(232, 67, 147, 0.1)",
    borderRadius: 2,
    border: "1px solid rgba(232, 67, 147, 0.3)",
  },

  dateLabel: {
    fontSize: "0.875rem",
    color: "#A0AEC0",
    mb: 1,
  },

  dateValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#E84393",
  },

  feeBox: {
    textAlign: "center",
    p: 3,
    background: "rgba(108, 92, 231, 0.1)",
    borderRadius: 2,
    border: "1px solid rgba(108, 92, 231, 0.3)",
  },

  feeAmount: {
    fontSize: "2.5rem",
    fontWeight: 900,
    color: "#6C5CE7",
    lineHeight: 1,
    mb: 1,
  },

  feeLabel: {
    fontSize: "0.875rem",
    color: "#A0AEC0",
  },

  paymentSplitCard: {
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(30px)",
    borderRadius: "32px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    overflow: "hidden",
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: "stretch",
    mt: 3,
    transition: "all 0.4s ease",
    "&:hover": {
      borderColor: "rgba(232, 67, 147, 0.4)",
      boxShadow: "0 20px 50px rgba(0, 0, 0, 0.3)",
    }
  },

  paymentContentSide: {
    flex: { xs: "1", md: "0.5" },
    p: { xs: 2.5, md: 3 },
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    height: "100%",
  },

  paymentImageSide: {
    flex: { xs: "1", md: "0.5" },
    background: "linear-gradient(135deg, rgba(232, 67, 147, 0.1) 0%, rgba(108, 92, 231, 0.1) 100%)",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
    p: 0,
    position: "relative",
    overflow: "hidden",
    height: "100%",
    borderLeft: { md: "1px solid rgba(255, 255, 255, 0.1)" },
    "&::before": {
      content: '""',
      position: "absolute",
      width: "100%",
      height: "100%",
      background: "radial-gradient(circle, rgba(232, 67, 147, 0.2) 0%, transparent 70%)",
      top: "-25%",
      left: "-25%",
    }
  },

  paymentIllustration: {
    width: "100%",
    height: { xs: "200px", md: "70vh" },
    objectFit: "cover",
    zIndex: 1,
  },

  paymentInfoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 1.2,
  },

  paymentDetailItem: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },

  paymentLabelNew: {
    fontSize: "0.7rem",
    color: "#E84393",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "1.2px",
  },

  paymentValueNew: {
    fontSize: "0.95rem",
    color: "#FFFFFF",
    fontWeight: 600,
  },

  paymentCopyBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: { xs: 0.8, md: 1 },
    borderRadius: "16px",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.08)",
      borderColor: "rgba(232, 67, 147, 0.3)",
    }
  },

  paymentIconBoxNew: {
    width: 36,
    height: 36,
    borderRadius: "8px",
    background: "rgba(232, 67, 147, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#E84393",
    mr: 1.5,
  },

  listItems: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },

  listItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 2,
    p: 1.5,
    borderRadius: 1,
    transition: "all 0.3s ease",
    "&:hover": {
      background: "rgba(232, 67, 147, 0.05)",
    },
  },

  listBullet: {
    color: "#E84393",
    fontSize: "1.5rem",
    lineHeight: 1,
    mt: -0.5,
  },

  listText: {
    color: "#CBD5E0",
    fontSize: "1rem",
    flex: 1,
  },

  // Events
  eventsGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
    gap: 3,
  },

  eventCategoryCard: {
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(20px)",
    borderRadius: 3,
    p: 3,
    border: "1px solid rgba(255, 255, 255, 0.1)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      border: "1px solid rgba(232, 67, 147, 0.5)",
      boxShadow: "0 10px 30px rgba(232, 67, 147, 0.2)",
    },
  },

  eventCategoryTitle: {
    fontSize: "1.3rem",
    fontWeight: 700,
    textAlign: "center",
    mb: 0.5,
  },

  eventCategoryCount: {
    fontSize: "0.875rem",
    color: "#A0AEC0",
    textAlign: "center",
    mb: 2,
    fontWeight: 600,
  },

  eventsList: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },

  eventItem: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    p: 1,
    borderRadius: 1,
    transition: "all 0.3s ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.03)",
    },
  },

  eventDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    flexShrink: 0,
  },

  eventName: {
    fontSize: "0.9rem",
    color: "#CBD5E0",
  },

  // Schedule
  scheduleList: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    mb: 4,
  },

  scheduleCard: {
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(20px)",
    borderRadius: 3,
    p: 3,
    border: "1px solid rgba(255, 255, 255, 0.1)",
    display: "flex",
    alignItems: "center",
    gap: 3,
    flexWrap: { xs: "wrap", md: "nowrap" },
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s ease",
    "&:hover": {
      border: "1px solid rgba(232, 67, 147, 0.5)",
      transform: "translateX(5px)",
    },
  },

  scheduleAccent: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
  },

  scheduleContent: {
    flex: 1,
    pl: 2,
  },

  scheduleZone: {
    fontSize: "1.2rem",
    fontWeight: 700,
    mb: 0.5,
  },

  scheduleUniversity: {
    fontSize: "1rem",
    color: "#FFFFFF",
    fontWeight: 600,
    mb: 0.5,
  },

  scheduleLocation: {
    fontSize: "0.875rem",
    color: "#A0AEC0",
    mb: 0.25,
  },

  scheduleContact: {
    fontSize: "0.875rem",
    color: "#A0AEC0",
  },

  scheduleDateBox: {
    textAlign: "right",
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },

  scheduleDates: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: "#FFFFFF",
  },

  nationalEventCard: {
    background: "linear-gradient(135deg, rgba(232, 67, 147, 0.2) 0%, rgba(108, 92, 231, 0.2) 100%)",
    backdropFilter: "blur(20px)",
    borderRadius: 3,
    p: { xs: 3, md: 5 },
    border: "2px solid rgba(232, 67, 147, 0.5)",
    textAlign: "center",
  },

  nationalEventTitle: {
    fontSize: { xs: "1.8rem", md: "2.5rem" },
    fontWeight: 900,
    color: "#FFFFFF",
    mb: 1,
  },

  nationalEventSubtitle: {
    fontSize: { xs: "1.5rem", md: "2rem" },
    fontWeight: 700,
    color: "#E84393",
    mb: 2,
  },

  nationalEventDate: {
    fontSize: { xs: "1.3rem", md: "1.5rem" },
    fontWeight: 700,
    color: "#FFFFFF",
    mb: 1,
  },

  nationalEventVenue: {
    fontSize: "1rem",
    color: "#CBD5E0",
  },

  // Campus
  campusGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
    gap: 3,
    mt: 3,
  },

  campusCard: {
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(20px)",
    borderRadius: 3,
    p: 3,
    border: "1px solid rgba(255, 255, 255, 0.1)",
    textAlign: "center",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      border: "1px solid rgba(232, 67, 147, 0.5)",
    },
  },

  campusCardTitle: {
    fontSize: "1.2rem",
    fontWeight: 700,
    mb: 1.5,
  },

  campusCardText: {
    fontSize: "0.95rem",
    color: "#CBD5E0",
    mb: 0.5,
  },

  // Downloads
  downloadsGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
    gap: 3,
  },

  downloadCard: {
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(20px)",
    borderRadius: 3,
    p: 3,
    border: "1px solid rgba(255, 255, 255, 0.1)",
  },

  downloadTitle: {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "#E84393",
    mb: 2,
  },

  downloadList: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },

  downloadItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    p: 1.5,
    borderRadius: 1,
    transition: "all 0.3s ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.05)",
    },
  },

  downloadItemText: {
    fontSize: "0.9rem",
    color: "#CBD5E0",
  },

  downloadLink: {
    color: "#E84393",
    fontSize: "0.875rem",
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },

  // Gallery
  galleryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: 2,
    mt: 3,
  },

  galleryItem: {
    aspectRatio: "1",
    background: "linear-gradient(135deg, rgba(232, 67, 147, 0.1) 0%, rgba(108, 92, 231, 0.1) 100%)",
    borderRadius: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(232, 67, 147, 0.2)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    overflow: "hidden",
    "&:hover": {
      border: "1px solid rgba(232, 67, 147, 0.5)",
      boxShadow: "0 8px 25px rgba(232, 67, 147, 0.3)",
    },
  },

  galleryNumber: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#A0AEC0",
  },

  hashtagChip: {
    fontSize: "1rem",
    fontWeight: 700,
    px: 3,
    py: 2.5,
    borderRadius: 3,
    border: "2px solid",
  },

  // Mobile Drawer
  mobileDrawer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    background: "linear-gradient(180deg, #0F0F23 0%, #1A1A3E 100%)",
  },

  mobileDrawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    p: 2.5,
    borderBottom: "2px solid rgba(232, 67, 147, 0.3)",
    background: "linear-gradient(135deg, #E84393 0%, #6C5CE7 100%)",
  },

  closeButton: {
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },

  mobileDrawerMenu: {
    flex: 1,
    overflowY: "auto",
    py: 2,
  },

  mobileDrawerMenuItem: {
    px: 3,
    py: 2,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: "rgba(232, 67, 147, 0.1)",
    },
  },

  mobileDrawerMenuItemActive: {
    backgroundColor: "rgba(232, 67, 147, 0.2)",
    borderLeft: "4px solid #E84393",
    color: "#E84393",
  },

  // Footer
  footer: {
    background: "linear-gradient(180deg, #0A0A1E 0%, #05050F 100%)",
    borderTop: "1px solid rgba(232, 67, 147, 0.2)",
    py: 6,
    mt: 8,
  },

  footerContent: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, 1fr)",
      md: "repeat(3, 1fr)",
      lg: "repeat(5, 1fr)",
    },
    gap: 4,
    mb: 4,
  },

  footerSection: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },

  footerTitle: {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "#E84393",
    mb: 2,
  },

  footerText: {
    fontSize: "0.9rem",
    color: "#A0AEC0",
    mb: 0.5,
  },

  footerLink: {
    color: "#A0AEC0",
    fontSize: "0.9rem",
    textDecoration: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      color: "#E84393",
      paddingLeft: "8px",
    },
  },

  socialIcon: {
    color: "#A0AEC0",
    backgroundColor: "rgba(232, 67, 147, 0.1)",
    transition: "all 0.3s ease",
    "&:hover": {
      color: "#FFFFFF",
      backgroundColor: "#E84393",
      transform: "translateY(-3px)",
    },
  },

  footerBottom: {
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
    pt: 3,
    textAlign: "center",
  },

  footerCopyright: {
    fontSize: "0.875rem",
    color: "#718096",
  },
};